// Finanças inteligentes (pedido 2026-10-21: "um menu bem legal igual à
// Agenda") — aba Finanças do Assistente IA: meses, saldo atual, entradas ×
// gastos, categorias (com limite), movimentações do mês agrupadas por dia e
// a IA embaixo (texto/voz) pra lançar gastos, entradas, saldo e categorias.
// Dados reais das finanças PESSOAIS do cliente (GET /ai-assistant/finance).
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Loader2, Pencil, Trash2, TrendingDown, TrendingUp, Wallet } from "lucide-react";
import { cn } from "@/lib/utils";
import { gpsDriveAiAssistantService, type AiMe } from "@/services/gpsdrive-ai-assistant.service";
import { AssistantChat } from "./assistant-chat";

const TZ = "America/Sao_Paulo";
const brl = (cents: number) => (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const currentMonth = () => new Date().toLocaleDateString("en-CA", { timeZone: TZ }).slice(0, 7);
function shiftMonth(ym: string, delta: number) {
  const [y, m] = ym.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1 + delta, 1)).toISOString().slice(0, 7);
}
const monthShort = (ym: string) =>
  new Date(`${ym}-15T12:00:00Z`).toLocaleDateString("pt-BR", { month: "short", timeZone: "UTC" }).replace(".", "");
const monthLong = (ym: string) => {
  const s = new Date(`${ym}-15T12:00:00Z`).toLocaleDateString("pt-BR", { month: "long", year: "numeric", timeZone: "UTC" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const dayTitle = (ymd: string) => {
  const s = new Date(`${ymd}T12:00:00Z`).toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "short", timeZone: "UTC" });
  return s.charAt(0).toUpperCase() + s.slice(1).replace(".", "");
};
const daysIn = (ym: string) => {
  const [y, m] = ym.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
};

interface Move {
  key: string;
  id: string;
  kind: "gasto" | "entrada";
  day: string;
  title: string;
  subtitle: string;
  emoji: string;
  cents: number;
}

export function SmartFinance({ me, keyboardInset = 0 }: { me: AiMe; keyboardInset?: number }) {
  const qc = useQueryClient();
  const [month, setMonth] = useState(currentMonth);
  const [category, setCategory] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<{ text: string; nonce: number } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const thisMonth = currentMonth();

  const data = useQuery({
    queryKey: ["ai-assistant", "expenses", "finance", month],
    queryFn: () => gpsDriveAiAssistantService.finance(month),
  });
  const f = data.data;
  const invalidate = () => {
    setConfirmDelete(null);
    qc.invalidateQueries({ queryKey: ["ai-assistant", "expenses"] });
  };
  const removeExpense = useMutation({ mutationFn: (id: string) => gpsDriveAiAssistantService.deleteExpense(id), onSuccess: invalidate });
  const removeIncome = useMutation({ mutationFn: (id: string) => gpsDriveAiAssistantService.deleteIncome(id), onSuccess: invalidate });

  const emojiOf = (name: string) => f?.categories.find((c) => c.name === name)?.emoji ?? "📦";
  const cats = (f?.categories ?? []).filter((c) => c.spentCents > 0 || c.monthlyLimitCents != null || c.custom);

  const moves = useMemo<Move[]>(() => {
    if (!f) return [];
    const out: Move[] = f.items.map((e) => ({
      key: `g-${e.id}`,
      id: e.id,
      kind: "gasto",
      day: e.spentOn,
      title: e.description,
      subtitle: e.category,
      emoji: emojiOf(e.category),
      cents: e.amountCents,
    }));
    for (const i of f.incomes) {
      if (!i.inThisMonth) continue;
      const day =
        i.kind === "fixa" && i.dayOfMonth
          ? `${f.month}-${String(Math.min(i.dayOfMonth, daysIn(f.month))).padStart(2, "0")}`
          : (i.receivedOn ?? `${f.month}-01`);
      out.push({
        key: `e-${i.id}`,
        id: i.id,
        kind: "entrada",
        day,
        title: i.description,
        subtitle: i.kind === "fixa" ? `Entrada fixa · todo dia ${i.dayOfMonth}` : "Entrada",
        emoji: i.kind === "fixa" ? "💼" : "💵",
        cents: i.amountCents,
      });
    }
    return out
      .filter((m) => !category || (m.kind === "gasto" && m.subtitle === category))
      .sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : a.kind === "entrada" ? -1 : 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, category]);

  const byDay = useMemo(() => {
    const map = new Map<string, Move[]>();
    for (const m of moves) map.set(m.day, [...(map.get(m.day) ?? []), m]);
    return [...map.entries()];
  }, [moves]);

  const spentPct = f && f.incomeCents > 0 ? Math.min(100, Math.round((f.expenseCents / f.incomeCents) * 100)) : null;
  const months = Array.from({ length: 6 }, (_, i) => shiftMonth(month, i - 4));

  const suggestions = [
    { label: "💸 Novo gasto", message: "Gastei R$ ", send: false },
    { label: "💵 Recebi", message: "Recebi R$ ", send: false },
    { label: `📊 Resumo de ${monthShort(month)}`, message: `Como estão minhas finanças em ${monthLong(month).toLowerCase()}?`, send: true },
    { label: "🏷️ Nova categoria", message: "Cria a categoria ", send: false },
    { label: "💰 Meu saldo", message: "Meu saldo atual é R$ ", send: false },
    { label: "💼 Salário fixo", message: "Meu salário fixo é de R$ ", send: false },
  ];

  return (
    <div className="ai-chat-bg flex min-h-0 flex-1 flex-col">
      {/* Meses */}
      <div className="border-b ai-border bg-white px-3 pb-3 pt-2 shadow-[0_6px_18px_-14px_rgba(15,23,42,0.5)] dark:border-slate-800 dark:bg-slate-900">
        <div className="mb-2 flex items-center gap-1">
          <button type="button" aria-label="Mês anterior" onClick={() => setMonth((m) => shiftMonth(m, -1))} className="grid h-8 w-8 place-items-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">
            <ChevronLeft className="h-5 w-5 text-slate-600 dark:text-slate-300" />
          </button>
          <span className="flex-1 text-center text-sm font-bold text-slate-800 dark:text-slate-100">{monthLong(month)}</span>
          <button type="button" aria-label="Próximo mês" onClick={() => setMonth((m) => shiftMonth(m, 1))} className="grid h-8 w-8 place-items-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">
            <ChevronRight className="h-5 w-5 text-slate-600 dark:text-slate-300" />
          </button>
          {month !== thisMonth && (
            <button type="button" onClick={() => setMonth(thisMonth)} className="ml-1 rounded-full ai-soft px-3 py-1 text-xs font-bold ai-text">
              Este mês
            </button>
          )}
        </div>
        <div className="grid grid-cols-6 gap-1">
          {months.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMonth(m)}
              aria-pressed={m === month}
              className={cn(
                "rounded-2xl py-2 text-center text-xs font-bold capitalize transition active:scale-95",
                m === month ? "ai-grad shadow-md" : "text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800",
                m !== month && m === thisMonth && "ring-2 ring-inset ai-border",
                m > thisMonth && m !== month && "opacity-40",
              )}
            >
              {monthShort(m)}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3">
        {/* Saldo + resumo */}
        <div className="relative overflow-hidden rounded-3xl ai-grad p-5 shadow-[0_14px_30px_-14px_rgba(15,23,42,0.8)]">
          <span className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/20 blur-xl" aria-hidden />
          <div className="relative flex items-center gap-2 text-xs font-semibold uppercase tracking-wider opacity-85">
            <Wallet className="h-4 w-4" /> Saldo atual
          </div>
          <div className="relative mt-1 text-[32px] font-extrabold leading-tight tabular-nums">{f?.balance ? brl(f.balance.cents) : "—"}</div>
          {!f?.balance && (
            <button
              type="button"
              onClick={() => setPrefill({ text: "Meu saldo atual é R$ ", nonce: Date.now() })}
              className="relative mt-1 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold ring-1 ring-white/30"
            >
              Informar meu saldo
            </button>
          )}
          <div className="relative mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-2xl bg-white/15 p-2.5 ring-1 ring-white/20">
              <div className="flex items-center gap-1 text-[11px] font-semibold opacity-85">
                <TrendingUp className="h-3.5 w-3.5" /> Entradas
              </div>
              <div className="truncate text-base font-extrabold tabular-nums">{brl(f?.incomeCents ?? 0)}</div>
            </div>
            <div className="rounded-2xl bg-white/15 p-2.5 ring-1 ring-white/20">
              <div className="flex items-center gap-1 text-[11px] font-semibold opacity-85">
                <TrendingDown className="h-3.5 w-3.5" /> Gastos
              </div>
              <div className="truncate text-base font-extrabold tabular-nums">{brl(f?.expenseCents ?? 0)}</div>
            </div>
          </div>
          <div className="relative mt-3 flex items-center justify-between text-xs">
            <span className="opacity-85">Sobra do mês</span>
            <span className="font-extrabold tabular-nums">{brl(f?.leftCents ?? 0)}</span>
          </div>
          {spentPct != null && (
            <div className="relative mt-1.5">
              <div className="h-2 overflow-hidden rounded-full bg-white/25">
                <div className={cn("h-full rounded-full", spentPct >= 90 ? "bg-red-300" : "bg-white")} style={{ width: `${Math.max(3, spentPct)}%` }} />
              </div>
              <div className="mt-1 text-[11px] opacity-80">{spentPct}% do que entrou já foi gasto</div>
            </div>
          )}
        </div>

        {data.isLoading && <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-slate-400" />}
        {data.error && <p className="rounded-2xl bg-red-50 p-3 text-sm text-red-700">Não foi possível carregar as finanças.</p>}

        {/* Categorias */}
        {cats.length > 0 && (
          <div>
            <div className="mb-2 flex items-center justify-between px-1">
              <h4 className="text-xs font-bold uppercase tracking-wider ai-text">Categorias</h4>
              {category && (
                <button type="button" onClick={() => setCategory(null)} className="text-xs font-semibold ai-text">
                  Ver todas
                </button>
              )}
            </div>
            <div className="-mx-4 flex gap-2.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none]">
              {cats.map((c) => {
                const over = c.monthlyLimitCents != null && c.spentCents > c.monthlyLimitCents;
                const pct = c.monthlyLimitCents ? Math.min(100, (c.spentCents / c.monthlyLimitCents) * 100) : null;
                const active = category === c.name;
                return (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => setCategory(active ? null : c.name)}
                    aria-pressed={active}
                    className={cn(
                      "w-[132px] shrink-0 rounded-2xl border bg-white p-3 text-left shadow-[0_8px_22px_-16px_rgba(15,23,42,0.55)] transition active:scale-95 dark:bg-slate-900",
                      active ? "border-[var(--ai)] ring-2 ring-[var(--ai)]" : "border-white/70 dark:border-slate-800",
                    )}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="grid h-8 w-8 place-items-center rounded-xl ai-soft text-base">{c.emoji}</span>
                      <span className="min-w-0 truncate text-[13px] font-bold text-slate-800 dark:text-slate-100">{c.name}</span>
                    </div>
                    <div className={cn("mt-2 text-sm font-extrabold tabular-nums", over ? "text-red-600" : "text-slate-900 dark:text-white")}>
                      {brl(c.spentCents)}
                    </div>
                    {pct != null ? (
                      <>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                          <div className={cn("h-full rounded-full", over ? "bg-red-500" : "bg-[var(--ai)]")} style={{ width: `${Math.max(4, pct)}%` }} />
                        </div>
                        <div className={cn("mt-0.5 text-[10.5px]", over ? "font-semibold text-red-600" : "text-slate-400")}>
                          {over ? "Passou do limite" : `de ${brl(c.monthlyLimitCents!)}`}
                        </div>
                      </>
                    ) : (
                      <div className="mt-0.5 text-[10.5px] text-slate-400">sem limite</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Movimentações */}
        <div>
          <h4 className="mb-2 px-1 text-xs font-bold uppercase tracking-wider ai-text">
            {category ? `Gastos em ${category}` : "Movimentações"}
          </h4>
          {!data.isLoading && moves.length === 0 && (
            <div className="flex flex-col items-center rounded-3xl border border-dashed ai-border bg-white/70 px-6 py-8 text-center dark:bg-slate-900/60">
              <span className="grid h-14 w-14 place-items-center rounded-2xl ai-soft text-2xl">🪙</span>
              <div className="mt-3 font-bold text-slate-800 dark:text-slate-100">Nada lançado {category ? "nessa categoria" : "neste mês"}</div>
              <p className="mt-1 text-sm text-slate-500">Diga para a IA — ex.: “gastei 45 no almoço” ou “recebi 300 de um freela”.</p>
            </div>
          )}
          <div className="space-y-3">
            {byDay.map(([day, items]) => (
              <section key={day} className="rounded-3xl bg-white p-3 shadow-[0_8px_24px_-16px_rgba(15,23,42,0.45)] dark:bg-slate-900">
                <div className="mb-1 flex items-center justify-between px-1">
                  <span className="text-xs font-bold text-slate-500">{dayTitle(day)}</span>
                  <span className="text-xs font-semibold tabular-nums text-slate-400">
                    {(() => {
                      const net = items.reduce((s, m) => s + (m.kind === "entrada" ? m.cents : -m.cents), 0);
                      return `${net >= 0 ? "+" : "−"} ${brl(Math.abs(net))}`;
                    })()}
                  </span>
                </div>
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {items.map((m) => (
                    <div key={m.key} className="py-2">
                      <div className="flex items-center gap-3">
                        <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl text-lg", m.kind === "entrada" ? "bg-emerald-50 dark:bg-emerald-950/40" : "ai-soft")}>
                          {m.emoji}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-semibold text-slate-900 dark:text-white">{m.title}</div>
                          <div className="truncate text-xs text-slate-500">{m.subtitle}</div>
                        </div>
                        <div className={cn("font-extrabold tabular-nums", m.kind === "entrada" ? "text-emerald-600" : "text-slate-900 dark:text-white")}>
                          {m.kind === "entrada" ? "+" : "−"} {brl(m.cents)}
                        </div>
                      </div>
                      <div className="mt-1.5 flex justify-end gap-1.5">
                        {confirmDelete === m.key ? (
                          <>
                            <button
                              type="button"
                              onClick={() => (m.kind === "gasto" ? removeExpense : removeIncome).mutate(m.id)}
                              className="rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white"
                            >
                              Confirmar exclusão
                            </button>
                            <button type="button" onClick={() => setConfirmDelete(null)} className="rounded-full px-2 py-1 text-xs text-slate-500">
                              Voltar
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                setPrefill({
                                  text:
                                    m.kind === "gasto"
                                      ? `Corrige o gasto "${m.title}" de ${brl(m.cents)}: `
                                      : `Corrige a entrada "${m.title}" de ${brl(m.cents)}: `,
                                  nonce: Date.now(),
                                })
                              }
                              className="inline-flex items-center gap-1 rounded-full border ai-border px-2.5 py-0.5 text-[11px] font-semibold ai-text"
                            >
                              <Pencil className="h-3 w-3" /> Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmDelete(m.key)}
                              className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-0.5 text-[11px] font-semibold text-red-600 dark:border-red-900"
                            >
                              <Trash2 className="h-3 w-3" /> Excluir
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>

      {/* IA das finanças (texto ou voz) */}
      <div className="border-t ai-border bg-white/95 dark:border-slate-800 dark:bg-slate-950/95">
        <AssistantChat
          me={me}
          compact
          conversationId={conversationId}
          onConversationChange={setConversationId}
          suggestions={suggestions}
          prefill={prefill}
          placeholder="Peça à IA: “gastei 50 de gasolina”…"
          bottomInset={keyboardInset}
          compactBottom
        />
      </div>
    </div>
  );
}
